// @flow
import React, { Component } from 'react';
import SubmitBox from 'components/write/SubmitBox';
import SelectCategory from 'components/write/SelectCategory';
import InputTags from 'components/write/InputTags';
import InputBadges from 'components/write/InputBadges';
import WriteConfigureThumbnail from 'components/write/WriteConfigureThumbnail';
import SubmitBoxAdditional from 'components/write/SubmitBoxAdditional/SubmitBoxAdditional';
import { connect } from 'react-redux';
import type { State } from 'store';
import { BaseActions, ListingActions, WriteActions } from 'store/actionCreators';
import type { Categories, Meta, PostData } from 'store/modules/write';
import axios from 'axios';
import queryString from 'query-string';
import storage from 'lib/storage';
import { escapeForUrl } from 'lib/common';
import { type ContextRouter, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import WriteVisibilitySelect from 'components/write/WriteVisibilitySelect';
import WriteSeriesConfigureContainer from './WriteSeriesConfigureContainer';
import SubmitBoxSeriesContainer from './SubmitBoxSeriesContainer';

type Props = {
  open: boolean,
  categories: ?Categories,
  tags: string[],
  badges: string[],
  title: string,
  body: string,
  postData: ?PostData,
  uploadUrl: ?string,
  imagePath: ?string,
  uploadId: ?string,
  thumbnail: ?string,
  additional: boolean,
  meta: Meta,
  username: ?string,
  urlSlug: ?string,
  isPrivate: boolean,
  seriesMode: boolean,
  seriesId: ?string,
} & ContextRouter;

class SubmitBoxContainer extends Component<Props> {
  query = () => queryString.parse(this.props.location.search);

  initialize = async () => {
    try {
      await WriteActions.listCategories();
    } catch (e) {
      console.log(e);
    }
  };

  componentDidMount() {
    this.initialize();
    const savedCodeTheme = storage.get('codeTheme');

    if (savedCodeTheme) {
      WriteActions.setMetaValue({ name: 'code_theme', value: savedCodeTheme });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.open && this.props.open) {
      this.initialize();
    }
  }

  uploadImage = async (file: any) => {
    if (!file) return;
    if (file.size > 1024 * 1024 * 10) return;
    const fileTypeRegex = /^image\/(.*?)/;
    if (!fileTypeRegex.test(file.type)) return;
    if (file.type.indexOf('gif') > 0) return;
    // temp save post if not released
    if (!this.props.postData) {
      await WriteActions.setTempData(); // nextTick
      const { title, body, tags, badges, categories, thumbnail, urlSlug, isPrivate, seriesId } = this.props;
      const activeCategories = (() => {
        if (!categories || categories.length === 0) return [];
        return categories.filter(c => c.active).map(c => c.id);
      })();
      try {
        switch (this.query().type) {
          case 'post':
            await WriteActions.writePost({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'product':
            await WriteActions.writeProduct({
              title,
              body,
              tags,
              badges,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'brand':
            await WriteActions.writeBrand({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'banner':
            await WriteActions.writeBanner({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'event':
            await WriteActions.writeEvent({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'letter':
            await WriteActions.writeLetter({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'quote':
            await WriteActions.writeQuote({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'partner':
            await WriteActions.writePartner({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          default:
            await WriteActions.writePost({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
        }
      } catch (e) {
        console.log(e);
      }
    }
    if (!this.props.postData) return;
    const { id } = this.props.postData;
    try {
      const filename = escapeForUrl(file.name);
      try {
        switch (this.query().type) {
          case 'post':
            await WriteActions.createUploadUrl({ postId: id, filename });
            break;
          case 'product':
            await WriteActions.createProductUploadUrl({ productId: id, filename });
            break;
          case 'brand':
            await WriteActions.createBrandUploadUrl({ brandId: id, filename });
            break;
          case 'banner':
            await WriteActions.createBannerUploadUrl({ bannerId: id, filename });
            break;
          case 'event':
            await WriteActions.createEventUploadUrl({ eventId: id, filename });
            break;
          case 'letter':
            await WriteActions.createLetterUploadUrl({ letterId: id, filename });
            break;
          case 'quote':
            await WriteActions.createQuoteUploadUrl({ quoteId: id, filename });
            break;
          case 'partner':
            await WriteActions.createPartnerUploadUrl({ partnerId: id, filename });
            break;
          default:
            await WriteActions.createUploadUrl({ postId: id, filename });
        }
      } catch (e) {
        console.log(e);
      }
      WriteActions.setUploadStatus(true);
      if (!this.props.uploadUrl) return;
      await axios.put(this.props.uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        withCredentials: false,
        onUploadProgress: e => {
          if (window.nanobar) {
            window.nanobar.go((e.loaded / e.total) * 100);
          }
        },
      });
      if (!this.props.imagePath) return;
      WriteActions.setThumbnail(encodeURI(`https://images.cacheby.com/${this.props.imagePath}`));
    } catch (e) {
      console.log(e);
    }
    WriteActions.setUploadStatus(false);
  };

  onClearThumbnail = () => {
    WriteActions.setThumbnail(null);
  };
  onUploadClick = () => {
    const upload = document.createElement('input');
    upload.type = 'file';
    upload.onchange = e => {
      if (!upload.files) return;
      const file = upload.files[0];
      this.uploadImage(file);
    };
    upload.click();
  };

  onInsertTag = tag => {
    const { tags } = this.props;
    const processedTag = tag.trim();
    if (processedTag === '') return;
    if (tags.indexOf(processedTag) !== -1) return;
    if (processedTag.length > 25) return;
    WriteActions.insertTag(processedTag);
  };
  onRemoveTag = tag => {
    WriteActions.removeTag(tag);
  };

  onInsertBadge = badge => {
    const { badges } = this.props;
    const processedBadge = badge.trim();
    if (processedBadge === '') return;
    if (badges.indexOf(processedBadge) !== -1) return;
    if (processedBadge.length > 25) return;
    WriteActions.insertBadge(processedBadge);
  };
  onRemoveBadge = badge => {
    WriteActions.removeBadge(badge);
  };

  onClose = () => {
    WriteActions.closeSubmitBox();
    // WriteActions.resetMeta();
  };
  onToggleCategory = id => {
    WriteActions.toggleCategory(id);
  };
  onEditCategoryClick = () => {
    WriteActions.openCategoryModal();
    WriteActions.closeSubmitBox();
  };
  onSubmit = async () => {
    const {
      categories,
      tags,
      badges,
      body,
      title,
      postData,
      thumbnail,
      meta,
      urlSlug,
      isPrivate,
      seriesId,
    } = this.props;
    try {
      if (postData && this.query().edit_id) {
        switch (this.query().type) {
          case 'post':
            await WriteActions.updatePost({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 수정됐습니다',
            });
            break;
          case 'product':
            await WriteActions.updateProduct({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              badges,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '상품이 수정됐습니다',
            });
            break;
          case 'brand':
            await WriteActions.updateBrand({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '브랜드가 수정됐습니다',
            });
            break;
          case 'banner':
            await WriteActions.updateBanner({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '배너가 수정됐습니다',
            });
            break;
          case 'event':
            await WriteActions.updateEvent({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '이벤트가 수정됐습니다',
            });
            break;
          case 'letter':
            await WriteActions.updateLetter({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '뉴스레터가 수정됐습니다',
            });
            break;
          case 'quote':
            await WriteActions.updateQuote({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '견적요청이 수정됐습니다',
            });
            break;
          case 'partner':
            await WriteActions.updatePartner({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '파트너가 수정됐습니다',
            });
            break;
          default:
            await WriteActions.updatePost({
              id: postData.id,
              thumbnail,
              title,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 수정됐습니다',
            });
        }
      } else {
        switch (this.query().type) {
          case 'post':
            await WriteActions.writePost({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 등록됐습니다',
            });
            break;
          case 'product':
            await WriteActions.writeProduct({
              title,
              thumbnail,
              body,
              tags,
              badges,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '제품이 등록됐습니다',
            });
            break;
          case 'brand':
            await WriteActions.writeBrand({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '브랜드가 등록됐습니다',
            });
            break;
          case 'banner':
            await WriteActions.writeBanner({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '배너가 등록됐습니다',
            });
            break;
          case 'event':
            await WriteActions.writeEvent({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '이벤트가 등록됐습니다',
            });
            break;
          case 'letter':
            await WriteActions.writeLetter({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '뉴스레터가 등록됐습니다',
            });
            break;
          case 'quote':
            await WriteActions.writeQuote({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '견적요청이 등록됐습니다',
            });
            break;
          case 'partner':
            await WriteActions.writePartner({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '파트너가 등록됐습니다',
            });
            break;
          default:
            await WriteActions.writePost({
              title,
              thumbnail,
              body,
              tags,
              is_temp: false,
              categories: categories ? categories.filter(c => c.active).map(c => c.id) : [],
              meta,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 등록됐습니다',
            });
        }
      }

      // redirect to post after success
      if (!this.props.username || !this.props.postData) return;
      let postUrl = `/@${this.props.postData.user.username}/${this.props.postData &&
        this.props.postData.url_slug}`;
      switch (this.query().type) {
        case 'post':
          postUrl = `/@${this.props.postData.user.username}/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'product':
          postUrl = `/@${this.props.postData.user.username}/products/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'brand':
          postUrl = `/@${this.props.postData.user.username}/brands/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'banner':
          postUrl = `/@${this.props.postData.user.username}/banners/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'event':
          postUrl = `/@${this.props.postData.user.username}/events/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'letter':
          postUrl = `/@${this.props.postData.user.username}/letters/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'quote':
          postUrl = `/@${this.props.postData.user.username}/quotes/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        case 'partner':
          postUrl = `/@${this.props.postData.user.username}/partners/${this.props.postData &&
            this.props.postData.url_slug}`;
          break;
        default:
          postUrl = `/@${this.props.postData.user.username}/${this.props.postData &&
            this.props.postData.url_slug}`;
      }
      ListingActions.flushList();
      setTimeout(() => {
        this.props.history.push(postUrl);
      });
    } catch (e) {
      BaseActions.showToast({
        type: 'error',
        message: '등록 실패',
      });
    }
  };

  onTempSave = async () => {
    const {
      postData,
      title,
      body,
      tags,
      badges,
      categories,
      thumbnail,
      urlSlug,
      isPrivate,
      seriesId,
    } = this.props;

    const activeCategories = (() => {
      if (!categories || categories.length === 0) return [];
      return categories.filter(c => c.active).map(c => c.id);
    })();

    if (!title) {
      BaseActions.showToast({
        type: 'error',
        message: '타이틀을 입력해주세요.',
      });
      return;
    }
    if (!body) {
      BaseActions.showToast({
        type: 'error',
        message: '내용을 입력해주세요.',
      });
      return;
    }
    try {
      if (!postData) {
        switch (this.query().type) {
          case 'post':
            await WriteActions.writePost({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'product':
            await WriteActions.writeProduct({
              title,
              body,
              tags,
              badges,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'brand':
            await WriteActions.writeBrand({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'banner':
            await WriteActions.writeBanner({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'event':
            await WriteActions.writeEvent({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'letter':
            await WriteActions.writeLetter({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'quote':
            await WriteActions.writeQuote({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'partner':
            await WriteActions.writePartner({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          default:
            await WriteActions.writePost({
              title,
              body,
              tags,
              is_temp: true,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug || escapeForUrl(title),
              is_private: isPrivate,
              series_id: seriesId,
            });
        }
      }
      if (postData && postData.is_temp) {
        switch (this.query().type) {
          case 'post':
            await WriteActions.updatePost({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'product':
            await WriteActions.updateProduct({
              id: postData.id,
              title,
              body,
              tags,
              badges,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'brand':
            await WriteActions.updateBrand({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'banner':
            await WriteActions.updateBanner({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'event':
            await WriteActions.updateEvent({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'letter':
            await WriteActions.updateLetter({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'quote':
            await WriteActions.updateQuote({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          case 'partner':
            await WriteActions.updatePartner({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
          default:
            await WriteActions.updatePost({
              id: postData.id,
              title,
              body,
              tags,
              is_temp: postData.is_temp,
              thumbnail,
              categories: activeCategories,
              url_slug: urlSlug,
              is_private: isPrivate,
              series_id: seriesId,
            });
            break;
        }
      }
      if (this.props.postData) {
        switch (this.query().type) {
          case 'post':
            await WriteActions.tempSave({ title, body, postId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 임시저장 되었습니다',
            });
            break;
          case 'product':
            await WriteActions.tempSaveProduct({ title, body, productId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '제품이 임시저장 되었습니다',
            });
            break;
          case 'brand':
            await WriteActions.tempSaveBrand({ title, body, brandId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '브랜드가 임시저장 되었습니다',
            });
            break;
          case 'banner':
            await WriteActions.tempSaveBanner({ title, body, bannerId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '배너가가 임시저장 되었습니다',
            });
            break;
          case 'event':
            await WriteActions.tempSaveEvent({ title, body, eventId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '이벤트가 임시저장 되었습니다',
            });
            break;
          case 'letter':
            await WriteActions.tempSaveLetter({ title, body, letterId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '뉴스레터가 임시저장 되었습니다',
            });
            break;
          case 'quote':
            await WriteActions.tempSaveQuote({ title, body, quoteId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '견적요청이 임시저장 되었습니다',
            });
            break;
          case 'partner':
            await WriteActions.tempSavePartner({ title, body, partnerId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '파트너가 임시저장 되었습니다',
            });
            break;
          default:
            await WriteActions.tempSave({ title, body, postId: this.props.postData.id });
            BaseActions.showToast({
              type: 'success',
              message: '포스트가 임시저장 되었습니다',
            });
        }
      }
    } catch (e) {
      console.log('임시저장 실패', e);
      BaseActions.showToast({
        type: 'error',
        message: '임시저장 실패',
      });
    }
  };

  onToggleAdditionalConfig = () => {
    WriteActions.toggleAdditionalConfig();
  };
  onCancelAdditionalConfig = () => {
    WriteActions.toggleAdditionalConfig();
    // WriteActions.resetMeta();
  };
  onChangeShortDescription = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'short_description', value: e.target.value });
  };
  onChangePrice = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'price', value: e.target.value });
  };
  onChangeSupplier = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'supplier', value: e.target.value });
  };
  onChangeProductUrl = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'productUrl', value: e.target.value });
  };
  onChangePartnerUrl = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'partnerUrl', value: e.target.value });
  };
  onChangeBrandUrl = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'brandUrl', value: e.target.value });
  };
  onChangeQuoteStatus = (isPrivate: boolean) => {
    WriteActions.setMetaValue({ name: 'quoteStatus', value: isPrivate });
  };
  onChangeEventAt = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.setMetaValue({ name: 'eventAt', value: e.target.value });
  };
  onChangeEventStatus = (isPrivate: boolean) => {
    WriteActions.setMetaValue({ name: 'eventStatus', value: isPrivate });
  };
  onChangeCodeTheme = (e: SyntheticInputEvent<HTMLSelectElement>) => {
    WriteActions.setMetaValue({ name: 'code_theme', value: e.target.value });
  };
  onChangeUrlSlug = (e: SyntheticInputEvent<HTMLInputElement>) => {
    WriteActions.changeUrlSlug(e.target.value);
  };
  onConfirmAdditionalConfig = () => {
    WriteActions.toggleAdditionalConfig();
    storage.set('codeTheme', this.props.meta.code_theme);
  };
  onSelectVisibility = (isPrivate: boolean) => {
    WriteActions.setVisibility(isPrivate);
  };

  render() {
    const {
      onClose,
      onToggleCategory,
      onInsertTag,
      onRemoveTag,
      onInsertBadge,
      onRemoveBadge,
      onSubmit,
      onEditCategoryClick,
      onUploadClick,
      onClearThumbnail,
      onToggleAdditionalConfig,
      onChangeShortDescription,
      onChangePrice,
      onChangeSupplier,
      onChangeProductUrl,
      onChangePartnerUrl,
      onChangeBrandUrl,
      onChangeQuoteStatus,
      onChangeEventAt,
      onChangeEventStatus,
      onChangeUrlSlug,
      onCancelAdditionalConfig,
      onConfirmAdditionalConfig,
    } = this;

    const {
      body,
      open,
      categories,
      tags,
      badges,
      postData,
      thumbnail,
      additional,
      meta,
      username,
      urlSlug,
      isPrivate,
      seriesMode,
      rule,
    } = this.props;

    let postLink = username && postData && `/@${username}/${postData.url_slug}`;
    switch (this.query().type) {
      case 'post':
        postLink = username && postData && `/@${username}/${postData.url_slug}`;
        break;
      case 'product':
        postLink = username && postData && `/@${username}/products/${postData.url_slug}`;
        break;
      case 'brand':
        postLink = username && postData && `/@${username}/brands/${postData.url_slug}`;
        break;
      case 'banner':
        postLink = username && postData && `/@${username}/banners/${postData.url_slug}`;
        break;
      case 'event':
        postLink = username && postData && `/@${username}/events/${postData.url_slug}`;
        break;
      case 'letter':
        postLink = username && postData && `/@${username}/letters/${postData.url_slug}`;
        break;
      case 'quote':
        postLink = username && postData && `/@${username}/quotes/${postData.url_slug}`;
        break;
      case 'partner':
        postLink = username && postData && `/@${username}/partners/${postData.url_slug}`;
        break;
      default:
        postLink = username && postData && `/@${username}/${postData.url_slug}`;
    }

    return (
      <SubmitBox
        postLink={postLink}
        onEditCategoryClick={onEditCategoryClick}
        selectCategory={<SelectCategory categories={categories} onToggle={onToggleCategory} />}
        inputTags={<InputTags tags={tags} onInsert={onInsertTag} onRemove={onRemoveTag} />}
        inputBadges={<InputBadges badges={badges} onInsert={onInsertBadge} onRemove={onRemoveBadge} />}
        configureThumbnail={
          <WriteConfigureThumbnail
            thumbnail={thumbnail}
            onUploadClick={onUploadClick}
            onClearThumbnail={onClearThumbnail}
          />
        }
        configureSeries={<WriteSeriesConfigureContainer />}
        visibilitySelect={
          <WriteVisibilitySelect onSelect={this.onSelectVisibility} isPrivate={isPrivate} />
        }
        visible={open}
        onClose={onClose}
        onSubmit={onSubmit}
        isEdit={!!postData && !postData.is_temp}
        onTempSave={this.onTempSave}
        onToggleAdditionalConfig={onToggleAdditionalConfig}
        rule={rule}
        additional={
          additional && (
            <SubmitBoxAdditional
              body={body}
              meta={meta}
              urlSlug={urlSlug}
              username={username || ''}
              realMeta={postData && postData.meta}
              onChangeShortDescription={onChangeShortDescription}
              onChangePrice={onChangePrice}
              onChangeSupplier={onChangeSupplier}
              onChangeProductUrl={onChangeProductUrl}
              onChangePartnerUrl={onChangePartnerUrl}
              onChangeBrandUrl={onChangeBrandUrl}
              onChangeQuoteStatus={onChangeQuoteStatus}
              onChangeEventAt={onChangeEventAt}
              onChangeEventStatus={onChangeEventStatus}
              onChangeUrlSlug={onChangeUrlSlug}
              onCancel={onCancelAdditionalConfig}
              onConfirm={onConfirmAdditionalConfig}
              type={this.query().type}
            />
          )
        }
        series={seriesMode && <SubmitBoxSeriesContainer />}
        type={this.query().type}
      />
    );
  }
}

const enhance = compose(
  connect(
    ({ write, user }: State) => ({
      open: write.submitBox.open,
      categories: write.submitBox.categories,
      tags: write.submitBox.tags,
      badges: write.submitBox.badges,
      body: write.body,
      title: write.title,
      postData: write.postData,
      uploadUrl: write.upload.uploadUrl,
      imagePath: write.upload.imagePath,
      uploadId: write.upload.id,
      thumbnail: write.thumbnail,
      additional: write.submitBox.additional,
      meta: write.meta,
      username: user.user && user.user.username,
      rule: user.user && user.user.rule,
      urlSlug: write.submitBox.url_slug,
      isPrivate: write.submitBox.is_private,
      seriesMode: write.seriesModal.visible,
      seriesId: write.submitBox.series ? write.submitBox.series.id : null,
    }),
    () => ({}),
  ),
  withRouter,
);
export default enhance(SubmitBoxContainer);



// WEBPACK FOOTER //
// ./src/containers/write/SubmitBoxContainer.js